import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Mail, KeyRound, LogIn, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { AuthShell } from "./AuthPage";

const LANGS = ["pt", "pt-PT", "en", "es", "fr", "it"];

export default function ThankYou() {
  const { t, lang, setLang } = useI18n();
  const [params] = useSearchParams();
  const pending = params.get("s") === "pending";
  const urlLang = (params.get("lang") || "").slice(0, 2).toLowerCase();

  useEffect(() => {
    if (LANGS.includes(urlLang) && urlLang !== lang) setLang(urlLang);
  }, [urlLang, lang, setLang]);

  useEffect(() => {
    api.post("/analytics/event", { event: "view_thank_you", meta: { status: pending ? "pending" : "approved", lang } }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = [
    { icon: Mail, title: t("thanks.step1_title"), text: t("thanks.step1_text") },
    { icon: KeyRound, title: t("thanks.step2_title"), text: t("thanks.step2_text") },
    { icon: LogIn, title: t("thanks.step3_title"), text: t("thanks.step3_text") },
  ];

  return (
    <AuthShell>
      <div className="glass rounded-3xl p-8 shadow-2xl" data-testid="thankyou-card">
        <div className="text-center">
          {pending
            ? <Clock className="mx-auto mb-4 h-14 w-14 text-xmas-gold" data-testid="thankyou-icon-pending" />
            : <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-xmas-pine" data-testid="thankyou-icon-approved" />}
          <p className="text-[11px] uppercase tracking-[0.3em] text-xmas-gold">MY CHRISTMAS</p>
          <h1 className="mt-2 font-display text-xl font-bold text-xmas-snow sm:text-2xl" data-testid="thankyou-title">
            {pending ? t("thanks.pending_title") : t("thanks.title")}
          </h1>
          <p className="mt-3 text-sm text-xmas-slate" data-testid="thankyou-text">
            {pending ? t("thanks.pending_text") : t("thanks.text")}
          </p>
        </div>

        <ol className="mt-7 space-y-4" data-testid="thankyou-steps">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3 rounded-2xl border border-xmas-gold/15 bg-xmas-navy/40 p-4" data-testid={`thankyou-step-${i + 1}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-xmas-gold/15 text-xmas-gold">
                <s.icon className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-xmas-snow">{i + 1}. {s.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-xmas-slate">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-5 text-center text-xs text-xmas-slate/80" data-testid="thankyou-spam">{t("thanks.spam")}</p>

        <Link to="/login" data-testid="thankyou-login" className="btn-gold mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider">
          {t("thanks.cta")} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/" data-testid="thankyou-home" className="mt-3 block text-center text-xs text-xmas-slate hover:text-xmas-gold">
          {t("pending.back")}
        </Link>
      </div>
    </AuthShell>
  );
}
